// §46/§46.4 — the ONE lens. Mounted above page routing, this provider owns
// the lens core (eventTypes, regions, regionNames, timeRange,
// customDateRange, activePreset, presetKeywords, search, curation) plus the
// preset pool; scenes render beneath it and read it through useLens(). Identity is
// structural: nothing syncs, so the lensCarry drift class (§43.2) cannot
// recur — lensCarry is RETIRED as of P2 (module deleted, slot cleared below).
//
// Types are the graph's exact types (Set/Set/Map) so adopting pages pass
// these values straight into the filter builders they already have.
//
// THREE-LAYER PERSISTENCE (§46.1 P1 contract):
//   (a) React state — session truth, the only thing components read.
//   (b) synchronous localStorage mirror ("lens-shell-v1") — read inside the
//       useState initializers so the FIRST paint is already the user's lens
//       (the globe-flash fix generalized), written through on every change.
//   (c) server (v2/lens/state) — durable, hydrated ONCE on mount and
//       reconciled QUIETLY. Precedence is MIRROR WINS: layer (b) is this
//       device's last interaction, so the server may only FILL fields the
//       boot snapshot never carried. The test is per field, but a mirror
//       this version wrote holds every key — so in practice the server
//       fills only when boot is absent, legacy, or partial.
//       Writes debounce 750ms (the map/graph cadence) and flush on unmount:
//       this provider is route-scoped, so navigation would otherwise cancel
//       the pending write and lose the edit for every OTHER device.
//
// THE BRUSH (§48.1/§48.2 C) is lens state on NO persistence layer — session
// only. See the brush block below for why neither the server wire nor the
// mirror may carry it (the clamp window arrives after the first data frame).
//
// PAGE MEMORIES (§46.1 P4) are a FOURTH, separate layer with its own boundary:
// pageDims.<page> auto-saves (debounced, silent) to a per-(lens, page) slot
// while a lens is active and auto-applies on arrival. They are per-user working
// memory, not lens identity — they never touch appliedSnapshot/"modified",
// never render a control, and a missing slot is a cold start (keep the current
// values and start remembering), never a reset.
//
// SEED-ONCE: while the server doc AND the mirror are both empty we hold the
// defaults and write NOTHING — a defaults doc would look like a real opinion
// on the next boot and lock the server out of reconciliation forever. In P1
// the graph page's own hydration (its gs-graph state doc) is still the
// fallback seeder: its setter calls make the lens non-default, which is what
// releases the write gate here. P2 moves seeding into this provider.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getGraphViews } from "../../../api/gsGraph";
import {
  getLensState,
  getPageState,
  putLensState,
  putPageState,
} from "../../../api/lens";
import { getTrendingPreset } from "../../../api/mapApi";
import { GLOBAL_PRESET_REF } from "./systemLenses";

const MIRROR_KEY = "lens-shell-v1";
// §43 carry retirement (§46.1 P2): the ferry slot has no writer and no reader
// left — the map and graph share this provider, and the feed adopts the mirror
// read-only. Cleared once on mount so a stale slot can't outlive the module.
const RETIRED_CARRY_KEY = "lens-carry-v1";
const SAVE_DEBOUNCE_MS = 750;

// "modified" = the live lens differs from the lens that was APPLIED (preset
// or explicit reset). Ported from GSGraphPage so the dot keeps telling the
// same truth; entityTypes rides along because it is part of what a saved lens
// applies (§46.4 page-dim tenant #1), and SEARCH rides along because a search
// is a lens edit — it selects documents on the server exactly like every other
// dimension and is written durably, so the dot has to admit it.
//
// Positional: new dimensions append at the END. The slots above are addressed
// by index (withSnapshotSlot), so reordering them would silently rewrite the
// wrong dimension.
export const lensSnapshot = (
  eventTypes,
  regions,
  timeRange,
  customDateRange,
  entityTypes,
  curation,
  search,
) =>
  JSON.stringify([
    [...eventTypes].sort(),
    [...regions].sort(),
    timeRange,
    customDateRange || null,
    [...(entityTypes || [])].sort(),
    curation || null,
    search || "",
  ]);

// Rewriting ONE positional slot keeps a silent change silent: the dot compares
// against the same lens core it did before, every other dimension included.
// Layout knowledge stays here, beside the snapshot that defines it.
const withSnapshotSlot = (snapshot, index, values) => {
  try {
    const parts = JSON.parse(snapshot);
    if (!Array.isArray(parts)) return snapshot;
    parts[index] = [...(values || [])].sort();
    return JSON.stringify(parts);
  } catch {
    return snapshot; // unparseable snapshot: leave the dot exactly as it was
  }
};

// A page MEMORY may carry entityTypes — the one page dim a saved lens also
// applies, so an applied memory adopts it without ever marking modified (§46
// boundary — page memories never light the dot).
const withSnapshotEntityTypes = (snapshot, entityTypes) =>
  withSnapshotSlot(snapshot, 4, entityTypes);

// The echo reconciliation below clears event types WITHOUT lighting the dot, so
// the applied snapshot moves with them — in place, so every other dimension
// keeps saying exactly what it was already saying.
const withSnapshotEventTypes = (snapshot, eventTypes) =>
  withSnapshotSlot(snapshot, 0, eventTypes);

const EMPTY_SET = new Set();

// ── the brush (§48.1, §48.2 C) ───────────────────────────────────────────────
// The brush IS the visible time slice: {from, to} in epoch SECONDS (the
// timestamp unit every scene and the timeline strip already speak), or null =
// THE WHOLE LOADED WINDOW — the degenerate compatibility state a lens boots in
// and returns to when the slice is cleared.
//
// The old point-playhead is DERIVED, not stored: `playheadTs = brush.to`, the
// LEADING EDGE. That choice (over the midpoint) is what makes the pre-C filters
// keep their exact meaning — every point consumer tests `ts <= playheadTs`
// ("everything up to now"), and during a roll the leading edge is what "now"
// means. A zero-width brush {t, t} is precisely the old playhead at t.
const brushOf = (value) => {
  if (!value || typeof value !== "object") return null;
  const from = Number(value.from);
  const to = Number(value.to);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return from <= to ? { from, to } : { from: to, to: from };
};

const sameBrush = (a, b) =>
  a === b || (!!a && !!b && a.from === b.from && a.to === b.to);

// THE CLAMP: brush ⊆ the loaded window, always — a brush that dangles outside
// the span the scenes actually hold would filter to nothing while looking
// engaged. Containment and ordering are enforced HERE; the ~2% minimum width is
// a DRAG constraint and lives in the strip, so a caller may still express the
// zero-width degenerate brush.
//
// A brush with no overlap left (the window moved on: a new timeRange, a page
// whose span is elsewhere) collapses to null — "whole window" — rather than
// being slid to an edge it never meant.
const clampBrush = (brush, win) => {
  if (!brush) return null;
  if (!win) return brush; // no span reported yet: re-clamped when one arrives
  const { min, max } = win;
  if (!(max > min)) return null;
  if (brush.to < min || brush.from > max) return null;
  const from = Math.max(min, Math.min(brush.from, max));
  const to = Math.min(max, Math.max(brush.to, min));
  return from === brush.from && to === brush.to ? brush : { from, to };
};

const windowOf = (min, max) => {
  const lo = Number(min);
  const hi = Number(max);
  return Number.isFinite(lo) && Number.isFinite(hi) && hi > lo
    ? { min: lo, max: hi }
    : null;
};

const sameWindow = (a, b) =>
  a === b || (!!a && !!b && a.min === b.min && a.max === b.max);

// Roll speed is a multiplier, not a vocabulary: the strip owns the list it
// cycles, the provider only refuses values that would stall or slingshot a roll.
const speedOf = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n <= 16 ? n : 1;
};

// §46.3 d6 — curation is SINGLE-select in v1: one of the reaction fields
// (feed.py:2225 like/dislike/catalog user-id arrays) or nothing at all. A
// closed vocabulary, unlike timeRange/preset kind: the server compiles it to
// one field clause, so an unknown token would be a silently unfiltered lens.
const CURATION_VALUES = ["liked", "disliked", "cataloged"];
const curationOf = (value) =>
  typeof value === "string" && CURATION_VALUES.includes(value) ? value : null;

const readMirror = () => {
  try {
    const raw = window.localStorage.getItem(MIRROR_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {}; // storage blocked: the mirror is an optimization, never an error
  }
};

const has = (doc, key) => Object.prototype.hasOwnProperty.call(doc || {}, key);

const strList = (value) =>
  Array.isArray(value) ? value.filter((x) => typeof x === "string") : [];

const dateRange = (value) =>
  value && typeof value === "object" && value.from
    ? { from: value.from, to: value.to }
    : null;

const presetRef = (value) =>
  value && typeof value === "object" && typeof value.kind === "string"
    ? { kind: value.kind, id: value.id ?? null }
    : null;

// Boot snapshot = the mirror, nothing else. The carry overlay is gone with the
// slot (§46.1 P2): one shared provider means there is no second page writing a
// lens this one has to adopt, so first paint reads exactly what this provider
// last wrote.
const readBoot = () => readMirror();

// ── page dims (§46.3 d2, §46.1 P4) ───────────────────────────────────────────
// The per-page WHITELISTS. A page section's controls live here and nowhere
// else, which is what makes a memory a SNAPSHOT: the provider already holds
// the values, so P4 reads and writes one sub-doc instead of syncing anything.
// Each page owns its keys; the tables below are the only place that knows
// them, and the server's whitelist (routes/v2/lens.py) is their mirror image.
const VIEW_MODES = ["network", "topics", "globe"];
const TRENDING_MODES = ["surging", "volume", "hybrid"];
const FEED_SORTS = ["newest", "oldest"];

const flag = (value, fallback) =>
  typeof value === "boolean" ? value : fallback;
const oneOf = (value, allowed, fallback) =>
  allowed.includes(value) ? value : fallback;
const clampInt = (value, lo, hi, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, Math.round(n))) : fallback;
};

// wire → live. Types are the SCENE's types (entityTypes is a Set because every
// graph filter builder expects one); every key is total — an absent or
// malformed value takes the shipped default, never undefined.
const PAGE_DIM_READERS = {
  graph: (d) => ({
    entityTypes: new Set(strList(d.entityTypes)),
    minSupport: flag(d.minSupport, false),
    showComentions: flag(d.showComentions, false),
    renderLimit: clampInt(d.renderLimit, 100, 2000, 2000),
    trendingMode: oneOf(d.trendingMode, TRENDING_MODES, "hybrid"),
    viewMode: oneOf(d.viewMode, VIEW_MODES, "topics"),
  }),
  map: (d) => ({
    overlays: strList(d.overlays),
    favoriteSources: strList(d.favoriteSources),
  }),
  feed: (d) => ({
    sort: oneOf(d.sort, FEED_SORTS, "newest"),
    sources: strList(d.sources),
  }),
};

// live → wire. Key order is fixed by these literals: both the mirror and the
// memory dedupe compare SERIALIZED dims, so a reordered object would read as a
// change and re-PUT on every mount.
const PAGE_DIM_WRITERS = {
  graph: (d) => ({
    entityTypes: [...(d.entityTypes || [])],
    minSupport: !!d.minSupport,
    showComentions: !!d.showComentions,
    renderLimit: d.renderLimit,
    trendingMode: d.trendingMode,
    viewMode: d.viewMode,
  }),
  map: (d) => ({
    overlays: [...(d.overlays || [])],
    favoriteSources: [...(d.favoriteSources || [])],
  }),
  feed: (d) => ({ sort: d.sort, sources: [...(d.sources || [])] }),
};

const PAGES = Object.keys(PAGE_DIM_READERS);

const readPageDims = (value) => {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(
    PAGES.map((page) => [
      page,
      PAGE_DIM_READERS[page](
        source[page] && typeof source[page] === "object" ? source[page] : {},
      ),
    ]),
  );
};

const writePageDims = (dims) =>
  Object.fromEntries(
    PAGES.map((page) => [page, PAGE_DIM_WRITERS[page](dims?.[page] || {})]),
  );

const pageDimsWire = (page, dims) =>
  JSON.stringify(PAGE_DIM_WRITERS[page](dims || {}));

// The lens a memory belongs to, in the server's address space: a preset id, a
// DYNAMIC lens id (trending/latest/recommended — no preset doc to hang a slot
// on), or null for the draft. Never the {kind,id} object: the route keys slots
// by one string.
const lensSlotOf = (preset) => {
  if (!preset || preset.kind === "draft") return null;
  return preset.id || preset.kind || null;
};

// Wire dialect — one serialization for both persisted layers so reconciliation
// compares like with like. Keys mirror the preset "core" vocabulary
// (raw event keys, catalog area-ids, ISO "YYYY-MM-DD" custom spans).
//
// pageDims is deliberately NARROWED to graph.entityTypes here: the durable lens
// doc holds lens IDENTITY, and the rest of the page dims are per-lens MEMORIES
// with their own slots (§46 boundary). entityTypes stays because a saved lens
// applies it.
const serialize = (s) => ({
  eventTypes: [...s.eventTypes],
  regions: [...s.regions],
  regionNames: Object.fromEntries(s.regionNames),
  timeRange: s.timeRange,
  customDateRange: s.customDateRange || null,
  activePreset: s.activePreset || null,
  presetKeywords: s.presetKeywords,
  search: s.search,
  curation: s.curation || null,
  pageDims: { graph: { entityTypes: [...(s.pageDims.graph?.entityTypes || [])] } },
});

// The MIRROR carries the full page dims on top of the core. It is layer (b) —
// this device's last paint — and viewMode/overlays are exactly the dims a page
// would otherwise flash a default of while its memory slot is in flight.
//
// §48.2 C: the roll SPEED rides here and NOWHERE else. The BRUSH does NOT
// persist at all — it is session state (see the brush block): a restored range
// would be clamped only after the mounted strip reports its window.
const mirrorize = (s) => ({
  ...serialize(s),
  pageDims: writePageDims(s.pageDims),
  playbackSpeed: s.playbackSpeed,
  // The SERVER's updated_at from the last GET/PUT this device processed —
  // the sync anchor for MIRROR-WINS (GLOBAL_LENS_PLAN.md B2): on boot, a
  // server doc whose stamp differs from this one was written by ANOTHER
  // device since we last synced, and then the server wins wholesale.
  // Server-generated on both sides, so clock skew cannot bite. Omitted
  // (undefined) until a first sync — an old mirror without it keeps the
  // pure mirror-wins behavior.
  serverStamp: s.serverStamp || undefined,
});

const DEFAULTS = {
  eventTypes: new Set(),
  regions: new Set(),
  regionNames: new Map(),
  timeRange: "24h",
  customDateRange: null,
  // iso-fork: LOGIN LANDS ON GLOBAL (supersedes birr 2026-08-17's trending
  // boot) — Global is static (GLOBAL_LENS_PLAN.md): it IS these defaults
  // (all event types, all regions, 24h), so booting it never fires a
  // trending resolution. The choice stays DEVICE-LOCAL (mirror only, never
  // restored from the server doc; logout clears the mirror). Mid-session
  // reloads keep the mirror's choice on GS, where the preset UI exists.
  activePreset: { ...GLOBAL_PRESET_REF },
  presetKeywords: [],
  search: "",
  curation: null,
  pageDims: readPageDims(null),
  playbackSpeed: 1,
};
const DEFAULT_WIRE = JSON.stringify(serialize(DEFAULTS));
const DEFAULT_MIRROR = JSON.stringify(mirrorize(DEFAULTS));

// The core keys, i.e. everything the SEED-ONCE gate treats as "a real opinion".
// pageDims is excluded on purpose: a mirror holding nothing but page dims must
// not release the server write gate, or a defaults lens doc would lock the
// gs_graph seed out forever (the §46.1 SEED-ONCE contract).
const CORE_KEYS = [
  "eventTypes",
  "regions",
  "regionNames",
  "timeRange",
  "customDateRange",
  "activePreset",
  "presetKeywords",
  "search",
  "curation",
];

// A stored doc is NOT wire: it carries envelope keys (schemaVersion) and the
// server's key order, so it has to be re-expressed in the dialect before the
// string compare that suppresses redundant PUTs — comparing raw would make
// the first write of every session look like a change.
const wireOfDoc = (doc) =>
  serialize({
    eventTypes: new Set(strList(doc.eventTypes)),
    regions: new Set(strList(doc.regions)),
    regionNames: new Map(
      Object.entries(
        doc.regionNames && typeof doc.regionNames === "object"
          ? doc.regionNames
          : {},
      ),
    ),
    timeRange:
      typeof doc.timeRange === "string" ? doc.timeRange : DEFAULTS.timeRange,
    customDateRange: dateRange(doc.customDateRange),
    activePreset: presetRef(doc.activePreset),
    presetKeywords: strList(doc.presetKeywords),
    search: typeof doc.search === "string" ? doc.search : "",
    curation: curationOf(doc.curation),
    pageDims: {
      graph: { entityTypes: new Set(strList(doc.pageDims?.graph?.entityTypes)) },
    },
  });

// ── the trending echo (§46.4 / lensShellContract:109) ────────────────────────
// The echo is the server's RESOLVED trending filter. It is DISPLAY state and
// was never lens identity, but earlier builds wrote it into eventTypes on every
// map mount under trending, so durable lens docs still carry a machine-written
// list on a lens the user never touched. The one-time reconciliation in the
// provider clears exactly that, and nothing else.
//
// The mode matters: each trending mode resolves a different list. The MAP is
// what wrote the echo, so its own persisted choice (default "hybrid", same
// initializer as OsintMapHome's) is the only honest reading. A mode that
// disagrees just fails the equality below, which is a no-op.
const MAP_TRENDING_MODE_KEY = "osintMap.trendingMode";
const echoTrendingMode = () => {
  try {
    const mode = window.localStorage.getItem(MAP_TRENDING_MODE_KEY);
    return TRENDING_MODES.includes(mode) ? mode : "hybrid";
  } catch {
    return "hybrid"; // storage blocked: the map's default is what it used
  }
};

// EXACT set equality, both directions. A subset, a superset or one differing
// key is a list the USER authored — the negative control the reconciliation
// exists to respect.
const sameKeySet = (a, b) => {
  if (a.size !== b.size) return false;
  for (const key of a) if (!b.has(key)) return false;
  return true;
};

export const LensContext = createContext(null);

export const LensProvider = ({ children }) => {
  // Boot is read ONCE, before any state exists, and every initializer below
  // falls back to the shipped default when the field is absent.
  const [boot] = useState(readBoot);
  // "Something was already stored for this user" — the mirror. It gates the
  // writes below: a stored lens is a real opinion, defaults are not. Split in
  // two because the mirror now also carries page dims (see CORE_KEYS).
  const storedLensRef = useRef(CORE_KEYS.some((key) => has(boot, key)));
  const storedMirrorRef = useRef(Object.keys(boot).length > 0);

  const [eventTypes, setEventTypes] = useState(
    () => new Set(strList(boot.eventTypes)),
  );
  const [regions, setRegions] = useState(() => new Set(strList(boot.regions)));
  const [regionNames, setRegionNames] = useState(() =>
    new Map(
      Object.entries(
        boot.regionNames && typeof boot.regionNames === "object"
          ? boot.regionNames
          : {},
      ),
    ),
  );
  const [timeRange, setTimeRange] = useState(() =>
    typeof boot.timeRange === "string" ? boot.timeRange : DEFAULTS.timeRange,
  );
  const [customDateRange, setCustomDateRange] = useState(() =>
    dateRange(boot.customDateRange),
  );
  const [activePreset, setActivePreset] = useState(() => {
    // Non-GS variants hide the lens preset UI (LensShell), so a mirror that
    // stashed another lens (e.g. trending) would be stuck with no way back —
    // there, always boot the default. GS keeps the device's last choice.
    if (process.env.REACT_APP_IS_GS !== "true") {
      return { ...DEFAULTS.activePreset };
    }
    return presetRef(boot.activePreset) || { ...DEFAULTS.activePreset };
  });
  const [presetKeywords, setPresetKeywords] = useState(() =>
    strList(boot.presetKeywords),
  );
  const [search, setSearch] = useState(() =>
    typeof boot.search === "string" ? boot.search : "",
  );
  // §46.1 P3 — curation is a CORE lens dimension (null = every document), so
  // it rides the mirror, the durable doc and the modified dot exactly like the
  // window does. Pages compile it into the filter body they already send.
  const [curation, setCuration] = useState(() => curationOf(boot.curation));
  // B2 sync anchor (see mirrorize): the server's updated_at as of this
  // device's last sync. Not a lens dimension — never in CORE_KEYS.
  const [serverStamp, setServerStamp] = useState(() =>
    typeof boot.serverStamp === "string" && boot.serverStamp
      ? boot.serverStamp
      : null,
  );
  // §48.1/§48.2 C — THE BRUSH. It ABSORBS B's point-playhead: the point is a
  // derived read (`playheadTs`, below), not a second field that could disagree
  // with the range. One writer at a time by construction — exactly one
  // TimelineStrip is mounted (the shell's, or the map's own until its timeline
  // joins the shell).
  //
  // PERSISTENCE — NONE. Session state only:
  //   (1) the durable doc (layer c) stays lens IDENTITY. A display slice is not
  //       a selection criterion (§48.1 "composes orthogonally");
  //   (2) the mirror (layer b) can't carry it either. A restored range is in
  //       absolute epoch seconds while the clamp authority (`brushWindow`) is
  //       reported LATER by the mounted strip, so a restored brush would filter
  //       the first data frame through a window nobody has vouched for — and on
  //       a page that never reports one (no timed events, no server timeline) it
  //       would never be clamped at all while the brush UI stays hidden. Boot
  //       with no slice; the speed below is a preference and does ride the
  //       mirror.
  const [brush, setBrushState] = useState(null);
  // The loaded span the brush lives inside, REPORTED by the mounted strip
  // (setBrushWindow) — the provider cannot derive it: timeRange is a request,
  // the span is what the scene actually holds. Null until a strip reports one.
  const [brushWindow, setBrushWindowState] = useState(null);
  // The clamp authority for the stable setters below; the state exists only so
  // consumers can render the span.
  const brushWindowRef = useRef(null);
  // The ROLL — §48.1: play means the fixed-width brush glides across the whole
  // loaded span. Session-only (nothing restores "was playing"); the speed is a
  // preference and rides the mirror.
  const [playbackRolling, setPlaybackRollingState] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState(() =>
    speedOf(has(boot, "playbackSpeed") ? boot.playbackSpeed : 1),
  );

  // Value or updater, like useState — and CLAMPED on every path, so no caller
  // can leave a brush dangling outside the loaded span.
  const setBrush = useCallback((next) => {
    setBrushState((cur) => {
      const raw = typeof next === "function" ? next(cur) : next;
      const clamped = clampBrush(brushOf(raw), brushWindowRef.current);
      return sameBrush(cur, clamped) ? cur : clamped;
    });
  }, []);

  // Back to the degenerate state: no slice, the whole window is visible.
  const clearBrush = useCallback(
    () => setBrushState((cur) => (cur === null ? cur : null)),
    [],
  );

  // The window MOVED (new timeRange, new page, a refetch that shifted the
  // span): re-clamp in the same commit so the brush is never outside the data
  // for even one paint. Idempotent — the strip re-reports on every render.
  const setBrushWindow = useCallback((min, max) => {
    const win = windowOf(min, max);
    if (sameWindow(brushWindowRef.current, win)) return;
    brushWindowRef.current = win;
    setBrushWindowState(win);
    setBrushState((cur) => {
      const clamped = clampBrush(cur, win);
      return sameBrush(cur, clamped) ? cur : clamped;
    });
  }, []);

  const setPlaybackRolling = useCallback(
    (value) =>
      setPlaybackRollingState((cur) => {
        const next = !!(typeof value === "function" ? value(cur) : value);
        return next === cur ? cur : next;
      }),
    [],
  );

  const setPlaybackSpeed = useCallback(
    (value) =>
      setPlaybackSpeedState((cur) => {
        const next = speedOf(typeof value === "function" ? value(cur) : value);
        return next === cur ? cur : next;
      }),
    [],
  );

  // §46.4/§46.1 P4: pageDims is invisible plumbing that makes page memories a
  // snapshot instead of a sync layer. It holds the FULL per-page whitelists
  // (PAGE_DIM_READERS); the memory engine below writes one page's sub-doc per
  // lens and applies it silently on arrival.
  const [pageDims, setPageDims] = useState(() => readPageDims(boot.pageDims));
  // Read by the async memory paths, which must merge against the LIVE dims
  // rather than the ones captured when their request was issued.
  const pageDimsRef = useRef(pageDims);
  pageDimsRef.current = pageDims;

  const setPageDim = useCallback((page, key, value) => {
    setPageDims((prev) => {
      const cur = prev[page] || {};
      const next = typeof value === "function" ? value(cur[key]) : value;
      if (next === cur[key]) return prev;
      return { ...prev, [page]: { ...cur, [key]: next } };
    });
  }, []);

  const entityTypes = pageDims.graph?.entityTypes || EMPTY_SET;
  const setEntityTypes = useCallback(
    (value) => setPageDim("graph", "entityTypes", value),
    [setPageDim],
  );

  const [appliedSnapshot, setAppliedSnapshot] = useState(() =>
    lensSnapshot(
      new Set(strList(boot.eventTypes)),
      new Set(strList(boot.regions)),
      typeof boot.timeRange === "string" ? boot.timeRange : DEFAULTS.timeRange,
      dateRange(boot.customDateRange),
      new Set(strList(boot.pageDims?.graph?.entityTypes)),
      curationOf(boot.curation),
      typeof boot.search === "string" ? boot.search : "",
    ),
  );

  // A restored lens is an APPLIED lens (graph's rule: the snapshot rebuilds
  // from the adopted values so the dot never lies after a reload).
  const markApplied = useCallback(
    (next) =>
      setAppliedSnapshot(
        lensSnapshot(
          next?.eventTypes ?? eventTypes,
          next?.regions ?? regions,
          next?.timeRange ?? timeRange,
          next?.customDateRange ?? customDateRange,
          next?.entityTypes ?? entityTypes,
          // Presets carry no curation in v1 (§46.3 d6), so an apply that omits
          // it MARKS THE LIVE VALUE APPLIED rather than resetting it: the user's
          // triage lens survives switching presets and never lights the dot.
          next?.curation ?? curation,
          // Search is the opposite case: no preset carries one and the applying
          // page clears the input in the SAME tick as this call, so the live
          // value here is still the query the user just left behind. An apply
          // that omits `search` therefore means "this lens has no search"; a
          // caller that keeps one has to name it.
          next?.search ?? "",
        ),
      ),
    [eventTypes, regions, timeRange, customDateRange, entityTypes, curation],
  );

  const modified =
    lensSnapshot(
      eventTypes,
      regions,
      timeRange,
      customDateRange,
      entityTypes,
      curation,
      search,
    ) !== appliedSnapshot;

  // ── preset pool (unified: saved lenses + map presets) ─────────────────────
  const [presets, setPresets] = useState([]);
  const [presetsReady, setPresetsReady] = useState(false);

  const refreshPresets = useCallback(
    () =>
      getGraphViews()
        .then((res) => {
          const d = res?.data;
          // Tolerate the pre-unified-pool API shape ({views, map_presets}) so a
          // UI/API deploy-order mismatch never blanks the lens dropdown.
          const pool = Array.isArray(d?.presets)
            ? d.presets
            : [
                ...(d?.views || []).map((v) => ({
                  ...v,
                  editable: true,
                  core: v.state?.core || {},
                  graph: v.state?.graph || {},
                })),
                ...(d?.map_presets || []).map((m) => ({ ...m, editable: false })),
              ];
          setPresets(pool);
          setPresetsReady(true);
        })
        .catch(() => setPresetsReady(true)),
    [],
  );
  useEffect(() => {
    refreshPresets();
  }, [refreshPresets]);

  // ── layer (c): server hydrate once, reconcile quietly ─────────────────────
  const [serverReady, setServerReady] = useState(false);
  const lastSavedRef = useRef("");
  // The last PUT's truncation report ({field: {sent, kept, cap}}), or null when
  // the durable doc holds everything this device sent. Exposed so a scene can
  // name the loss; the provider itself already stops believing it.
  const [lensTruncated, setLensTruncated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getLensState()
      .then((res) => {
        if (cancelled) return;
        const doc = res?.data?.state || null;
        // A SEEDED payload is derived from the old gs_graph doc — the server
        // holds no lens doc yet — so it must not seed the write dedupe: the
        // next PUT is exactly what makes the seed durable (§46.3 d1/d5).
        const seeded = res?.data?.seeded === true;
        lastSavedRef.current =
          doc && !seeded ? JSON.stringify(wireOfDoc(doc)) : "";
        // B2 (GLOBAL_LENS_PLAN.md): stamps are SERVER-generated on both sides
        // of the compare. A stored stamp that differs from the doc's means
        // ANOTHER DEVICE wrote since this mirror last synced — then the
        // server wins WHOLESALE (adopted as an applied lens, like any
        // restore). A missing local stamp is a pre-upgrade mirror and keeps
        // pure mirror-wins; a missing doc stamp (seeded) likewise. The one
        // self-race — our own flushed PUT whose response never recorded —
        // adopts a doc with our own content: a no-op by value.
        const stamp =
          typeof res?.data?.updated_at === "string" && res.data.updated_at
            ? res.data.updated_at
            : null;
        const localStamp =
          typeof boot.serverStamp === "string" && boot.serverStamp
            ? boot.serverStamp
            : null;
        if (stamp) setServerStamp(stamp);
        // PRISTINE LOGIN (birr 2026-08-17): a boot with NO mirror at all is a
        // fresh login (logout clears the mirror) or a new device — it adopts
        // NOTHING from the durable doc: clean Trending, no carried filters.
        // The doc still seeds the write dedupe and the sync stamp above, the
        // mirror keeps filters across reloads WITHIN a session, and the B2
        // stamp compare still serves live cross-device edits. Working
        // filters a user wants to keep across logins are PRESETS now. The
        // first edit after a pristine boot overwrites the doc — that discard
        // is the point.
        const pristine = Object.keys(boot).length === 0;
        if (pristine) return;
        if (doc && !seeded && stamp && localStamp && stamp !== localStamp) {
          const et = new Set(strList(doc.eventTypes));
          const rg = new Set(strList(doc.regions));
          const tr =
            typeof doc.timeRange === "string"
              ? doc.timeRange
              : DEFAULTS.timeRange;
          const cdr = dateRange(doc.customDateRange);
          const ent = new Set(strList(doc.pageDims?.graph?.entityTypes));
          const cur = curationOf(doc.curation);
          const sch = typeof doc.search === "string" ? doc.search : "";
          setEventTypes(et);
          setRegions(rg);
          setRegionNames(
            new Map(
              Object.entries(
                doc.regionNames && typeof doc.regionNames === "object"
                  ? doc.regionNames
                  : {},
              ),
            ),
          );
          setTimeRange(tr);
          setCustomDateRange(cdr);
          // activePreset stays DEVICE-LOCAL here too (see DEFAULTS): a B2
          // wholesale adopt takes the other device's FILTERS, not its lens
          // choice — mismatches read as authored overrides, which every page
          // already renders coherently.
          setPresetKeywords(strList(doc.presetKeywords));
          setSearch(sch);
          setCuration(cur);
          setEntityTypes(ent);
          setAppliedSnapshot(lensSnapshot(et, rg, tr, cdr, ent, cur, sch));
          return;
        }
        if (doc) {
          // Mirror wins, tested per field: boot is this device's last word, so
          // the server may only FILL what boot never carried. A mirror this
          // version wrote carries every key, so the per-field test earns its
          // keep on absent, legacy and partial boots.
          const et = has(boot, "eventTypes") ? null : new Set(strList(doc.eventTypes));
          const rg = has(boot, "regions") ? null : new Set(strList(doc.regions));
          if (et) setEventTypes(et);
          if (rg) setRegions(rg);
          if (!has(boot, "regionNames") && doc.regionNames) {
            setRegionNames(new Map(Object.entries(doc.regionNames)));
          }
          const tr =
            !has(boot, "timeRange") && typeof doc.timeRange === "string"
              ? doc.timeRange
              : null;
          if (tr) setTimeRange(tr);
          const cdr = has(boot, "customDateRange")
            ? null
            : dateRange(doc.customDateRange);
          if (cdr) setCustomDateRange(cdr);
          // activePreset is deliberately NOT filled from the server doc: the
          // active-lens choice is DEVICE-LOCAL (birr 2026-08-17 — login lands
          // on Trending; see DEFAULTS.activePreset). The doc still carries it
          // for other consumers; this device just doesn't adopt it.
          if (!has(boot, "presetKeywords") && Array.isArray(doc.presetKeywords)) {
            setPresetKeywords(strList(doc.presetKeywords));
          }
          // Tested against null, not truthiness: "" is a real stored search.
          const sch =
            !has(boot, "search") && typeof doc.search === "string"
              ? doc.search
              : null;
          if (sch !== null) setSearch(sch);
          const cur = has(boot, "curation") ? null : curationOf(doc.curation);
          if (cur) setCuration(cur);
          const ent =
            has(boot, "pageDims") || !doc.pageDims?.graph
              ? null
              : new Set(strList(doc.pageDims.graph.entityTypes));
          if (ent) setEntityTypes(ent);
          // Server-filled values arrive as an APPLIED lens, not as edits.
          setAppliedSnapshot(
            lensSnapshot(
              et || new Set(strList(boot.eventTypes)),
              rg || new Set(strList(boot.regions)),
              tr ||
                (typeof boot.timeRange === "string"
                  ? boot.timeRange
                  : DEFAULTS.timeRange),
              cdr || dateRange(boot.customDateRange),
              ent || new Set(strList(boot.pageDims?.graph?.entityTypes)),
              cur || curationOf(boot.curation),
              sch ?? (typeof boot.search === "string" ? boot.search : ""),
            ),
          );
        }
      })
      .catch(() => {
        /* offline/404: mirror + defaults still drive a working lens */
      })
      .finally(() => {
        if (cancelled) return;
        setServerReady(true);
      });
    return () => {
      cancelled = true;
    };
    // boot is a frozen snapshot; setters are stable — mount-only by design.
  }, [boot, setEntityTypes]);

  // regionNames is a LABEL SIDECAR for the selected ids, never a cache. A name
  // for an id the lens no longer holds can never be rendered, but it still
  // rides the wire and counts against the route's own 60-name cap — which then
  // evicts a LIVE label. So the map is pruned to the selection wherever the
  // selection came from: a deselect, a preset apply, or the capped lists this
  // device adopted back from the durable doc.
  useEffect(() => {
    setRegionNames((prev) => {
      if (!(prev instanceof Map) || !prev.size) return prev;
      let next = null;
      for (const id of prev.keys()) {
        if (regions.has(id)) continue;
        if (!next) next = new Map(prev);
        next.delete(id);
      }
      return next || prev; // nothing dropped: same Map, no re-render, no PUT
    });
  }, [regions]);

  // ── one-time echo reconciliation ──────────────────────────────────────────
  // Stopping the write does not undo the docs it already wrote, so a lens whose
  // event types are EXACTLY the live trending echo is read as what it is: never
  // authored. It is cleared, quietly — no dot, no apply, no reset of anything
  // else. Any other relationship to the echo (a subset, a superset, one extra
  // key) is a real edit and is left completely alone.
  //
  // Runs AT MOST ONCE per provider lifetime, and only against a lens that is
  // still exactly as applied: the moment the user edits anything the lens is
  // theirs and this must not touch it. It waits for serverReady so the compared
  // list is the reconciled boot lens, not a half-hydrated one, and the ordinary
  // debounced PUT that follows is what makes the cleanup durable.
  const echoReconciledRef = useRef(false);
  // The async half decides against the lens as it stands when the echo ARRIVES,
  // not as it stood when the request was issued — including `modified`, which
  // can turn true anywhere inside the round trip (a time range, a curation
  // click). The arrival guard has to carry every term the issue guard does.
  const echoLensRef = useRef(null);
  echoLensRef.current = { eventTypes, activePreset, modified };

  useEffect(() => {
    if (!serverReady || echoReconciledRef.current) return;
    if (activePreset?.kind !== "trending" || modified) return;
    if (!eventTypes.size) return;
    echoReconciledRef.current = true;
    getTrendingPreset(echoTrendingMode()).then(
      (res) => {
        const echo = new Set(strList(res?.data?.state?.eventTypes));
        if (!echo.size) return; // no echo resolved: never guess, do nothing
        const live = echoLensRef.current;
        if (live.activePreset?.kind !== "trending" || live.modified) return;
        if (!sameKeySet(live.eventTypes, echo)) return;
        setEventTypes(new Set());
        setAppliedSnapshot((prev) => withSnapshotEventTypes(prev, EMPTY_SET));
      },
      () => {
        /* trending unreachable: the lens keeps exactly what it stored */
      },
    );
  }, [serverReady, activePreset, eventTypes, modified]);

  // ── layers (b) + (c) write-through ────────────────────────────────────────
  const wire = useMemo(
    () =>
      serialize({
        eventTypes,
        regions,
        regionNames,
        timeRange,
        customDateRange,
        activePreset,
        presetKeywords,
        search,
        curation,
        pageDims,
      }),
    [eventTypes, regions, regionNames, timeRange, customDateRange, activePreset,
      presetKeywords, search, curation, pageDims],
  );
  const serialized = useMemo(() => JSON.stringify(wire), [wire]);
  // The seed-once gate: untouched defaults with nothing stored anywhere are
  // NOT an opinion and must not be written to the durable doc.
  const writable = serialized !== DEFAULT_WIRE || storedLensRef.current;

  // The mirror runs on its own gate: a page dim the user set IS this device's
  // last paint and must survive a reload, even under an untouched lens core.
  const mirrored = useMemo(
    () =>
      JSON.stringify(
        mirrorize({
          eventTypes,
          regions,
          regionNames,
          timeRange,
          customDateRange,
          activePreset,
          presetKeywords,
          search,
          curation,
          pageDims,
          playbackSpeed,
          serverStamp,
        }),
      ),
    [eventTypes, regions, regionNames, timeRange, customDateRange, activePreset,
      presetKeywords, search, curation, pageDims, playbackSpeed, serverStamp],
  );

  useEffect(() => {
    if (writable) storedLensRef.current = true;
    if (mirrored === DEFAULT_MIRROR && !storedMirrorRef.current) return;
    storedMirrorRef.current = true;
    try {
      window.localStorage.setItem(MIRROR_KEY, mirrored);
    } catch {
      /* storage full/blocked: session state still holds */
    }
  }, [mirrored, writable]);

  // §43 carry retirement (§46.1 P2): drop the dead slot once per mount. Not
  // gated on `writable` — the point is to remove storage nothing reads.
  useEffect(() => {
    try {
      window.localStorage.removeItem(RETIRED_CARRY_KEY);
    } catch {
      /* storage blocked: an orphan slot is inert either way */
    }
  }, []);

  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  // Save-path guard (§46.1 P1). PUT v2/lens/state is a FULL REPLACE, so two
  // writes in flight at once make the durable doc a race: a network reorder
  // leaves the OLDER body stored. Two invariants close it —
  //  (1) CHAIN: at most one PUT in flight. A commit raised while one is open
  //      is QUEUED (newest wins) and issued from that write's completion, so
  //      the server observes this device's writes in the order they happened.
  //  (2) SEQUENCE: every issue takes a monotonic seq, and a completion whose
  //      seq is no longer the latest describes a body the server has already
  //      overwritten — it may neither advance nor clear the dedupe (a late or
  //      duplicated failure of a superseded PUT must not retract a newer save
  //      or strand it behind a false "in flight").
  const seqRef = useRef(0);
  const inFlightRef = useRef(0);
  const queuedSaveRef = useRef(null);

  // The route CAPS eventTypes/regions (v2/lens.py MAX_*) and names what it
  // dropped. A cap the client ignores is the worst kind of lie: the selector
  // keeps rendering 300 regions while the durable doc holds 60, and the
  // optimistic dedupe below would call that body successfully saved. So the
  // stored lists are adopted as the truth, and the return says whether that
  // ADOPTION happened — adoption is what makes the sent body the wrong thing to
  // dedupe against, so it is the only case that may retract the dedupe.
  //
  // Adoption is skipped when a newer payload is pending or queued: that is an
  // edit made while this PUT was open, and the echo describes the body we
  // sent, not the one the user now holds. Nothing local changed, so retracting
  // the dedupe there would only re-send the same over-cap body to be truncated
  // again; the newer write goes out regardless and reports its own caps.
  //
  // The report state is owned here on every settled write — set on a cap,
  // cleared on a clean one. A FAILED write never reaches this (the caller
  // short-circuits), so a network error cannot erase the last known loss.
  const noteTruncation = useCallback((data) => {
    const report = data?.truncated;
    if (!report || typeof report !== "object" || !Object.keys(report).length) {
      setLensTruncated(null);
      return false;
    }
    setLensTruncated(report);
    if (pendingSaveRef.current || queuedSaveRef.current) return false;
    const stored = data.state || {};
    if (report.eventTypes) setEventTypes(new Set(strList(stored.eventTypes)));
    if (report.regions) setRegions(new Set(strList(stored.regions)));
    return true;
  }, []);

  // Fire-and-forget; a failed PUT clears the dedupe so the next change retries.
  const issueSave = useCallback(
    (payload) => {
      const seq = ++seqRef.current;
      inFlightRef.current = seq;
      // Optimistic dedupe: this PUT carries the live state, so an identical
      // re-render must not re-issue it. Reverted below if the write fails.
      lastSavedRef.current = payload.serialized;
      const settle = (ok, data) => {
        if (seq !== seqRef.current) return; // stale completion — invariant (2)
        inFlightRef.current = 0;
        // B2: every stored write advances the sync anchor, so the next boot's
        // stamp compare reads "in sync" unless ANOTHER device wrote after us.
        if (ok && typeof data?.updated_at === "string" && data.updated_at) {
          setServerStamp(data.updated_at);
        }
        // An ADOPTED cap replaced the local lists with the stored ones, so the
        // dedupe has to reopen: the adopted body is re-issued by the ordinary
        // debounce and stores clean, and the pass after it reports nothing. A
        // cap that could not be adopted leaves it alone (see noteTruncation).
        if (!ok || noteTruncation(data)) lastSavedRef.current = "";
        const queued = queuedSaveRef.current;
        if (queued) {
          queuedSaveRef.current = null;
          issueSave(queued);
        }
      };
      putLensState(payload.wire).then(
        (res) => settle(true, res?.data),
        () => settle(false),
      );
    },
    [noteTruncation],
  );

  // The queue drains from the completion handler, which outlives this
  // component — an edit queued behind an in-flight PUT still reaches the
  // server after the route unmounts.
  const commitSave = useCallback(
    (payload) => {
      pendingSaveRef.current = null;
      if (inFlightRef.current) {
        queuedSaveRef.current = payload;
        return;
      }
      issueSave(payload);
    },
    [issueSave],
  );

  useEffect(() => {
    // Never PUT before the GET resolves — an early write would clobber the
    // durable doc with pre-reconcile state.
    if (!serverReady || !writable) return undefined;
    if (serialized === lastSavedRef.current) {
      // Reverting to the saved value within the debounce window must also
      // retract the pending payload: the previous run's cleanup already
      // cancelled its timer, so a stale pending here would let the
      // unmount/tab-hide flush PUT an edit the user backed out of. The queued
      // payload retracts on the same test (R1 extended to the chain): it is a
      // superseded body that the in-flight write's completion would otherwise
      // send after the user returned to the saved value.
      pendingSaveRef.current = null;
      queuedSaveRef.current = null;
      return undefined;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = { serialized, wire };
    saveTimerRef.current = setTimeout(
      () => commitSave({ serialized, wire }),
      SAVE_DEBOUNCE_MS,
    );
    return () => clearTimeout(saveTimerRef.current);
  }, [serverReady, writable, serialized, wire, commitSave]);

  // ── page memories (§46.1 P4) ──────────────────────────────────────────────
  // A memory is one page's dims under one lens. It is INVISIBLE: nothing here
  // touches appliedSnapshot/modified, no control renders it, and a cold slot is
  // not an error — it means "keep what the scene already has and start
  // remembering". Everything below is keyed by SLOT (`${lensSlot}|${page}`), so
  // switching lens or page switches memory without any reconciliation.
  const [currentPage, setCurrentPage] = useState(null);
  // The mounted scene names itself (LensShell page= / useLensPage). Cleanup is
  // guarded: an exit animation can unmount the OLD page after the new one has
  // already registered, and an ungated reset would blank the live page.
  const registerPage = useCallback((page) => {
    if (!page || !PAGES.includes(page)) return undefined;
    setCurrentPage(page);
    return () => setCurrentPage((cur) => (cur === page ? null : cur));
  }, []);

  const lensSlot = useMemo(() => lensSlotOf(activePreset), [activePreset]);
  const slotId = currentPage ? `${lensSlot || "draft"}|${currentPage}` : null;

  const memoryLoadedRef = useRef(new Set()); // slots whose GET has settled
  // Settling a slot has to WAKE the save effect: a cold slot changes no state,
  // and without this an edit made while the GET was in flight would sit behind
  // the "not loaded yet" gate forever.
  const [memoryLoadTick, setMemoryLoadTick] = useState(0);
  const memoryAppliedRef = useRef({}); // page -> a stored slot was applied
  const memoryWireRef = useRef(new Map()); // slot -> last body known to the server
  const seededDimsRef = useRef(new Set()); // page.key -> a legacy seed landed

  // Legacy/cold-start seeding (§46.3 d5, "old state reads once"). A page's own
  // pre-P4 storage may still hold a dim; it writes here ONLY while the dim is
  // cold — a memory that has already been applied outranks it, and the FIRST
  // seed of a dim wins so the sync mirror keeps outranking async server state.
  const seedPageDim = useCallback(
    (page, key, value) => {
      if (!PAGES.includes(page) || value === undefined || value === null) return;
      const id = `${page}.${key}`;
      if (memoryAppliedRef.current[page] || seededDimsRef.current.has(id)) return;
      seededDimsRef.current.add(id);
      setPageDim(page, key, value);
    },
    [setPageDim],
  );

  // Fetch ONCE per slot per provider lifetime: re-entering a page whose lens is
  // unchanged already holds the live values, and a refetch could apply a body
  // older than an edit still in this device's debounce window.
  useEffect(() => {
    if (!serverReady || !currentPage || !slotId) return undefined;
    if (memoryLoadedRef.current.has(slotId)) return undefined;
    const page = currentPage;
    const slot = slotId;
    let cancelled = false;
    getPageState(page, lensSlot)
      .then((res) => {
        if (cancelled) return;
        const stored = res?.data?.dims;
        if (!stored || typeof stored !== "object" || !Object.keys(stored).length) {
          return; // cold start: the scene's current values become the memory
        }
        // PRESENT KEYS ONLY (the route's contract): a slot written before this
        // page grew a control must not reset the controls it never knew about,
        // so the stored keys are read over the LIVE dims, not over defaults.
        const live = pageDimsRef.current[page] || {};
        const merged = PAGE_DIM_READERS[page]({
          ...PAGE_DIM_WRITERS[page](live),
          ...stored,
        });
        // Pre-seed the dedupe with what we are ABOUT to hold: applying a slot
        // must not immediately PUT it straight back (the lastSaved pattern).
        memoryWireRef.current.set(slot, pageDimsWire(page, merged));
        memoryAppliedRef.current[page] = true;
        setPageDims((prev) => ({ ...prev, [page]: merged }));
        if (page === "graph") {
          // entityTypes is the one page dim a saved lens also applies, so the
          // applied snapshot adopts it — a memory may never light the dot.
          setAppliedSnapshot((prev) =>
            withSnapshotEntityTypes(prev, merged.entityTypes),
          );
        }
      })
      .catch(() => {
        /* offline/404: the scene keeps its values and starts remembering */
      })
      .finally(() => {
        if (cancelled) return;
        memoryLoadedRef.current.add(slot);
        setMemoryLoadTick((tick) => tick + 1);
      });
    return () => {
      cancelled = true;
    };
  }, [serverReady, currentPage, lensSlot, slotId]);

  const memoryTimerRef = useRef(null);
  const memoryPendingRef = useRef(null);
  // Same two invariants as the lens core save (CHAIN + SEQUENCE), per SLOT: a
  // PUT replaces one slot, so writes race only against their own slot and each
  // one carries its own chain.
  const memorySeqRef = useRef(new Map());
  const memoryInFlightRef = useRef(new Map());
  const memoryQueuedRef = useRef(new Map());

  const issueMemory = useCallback((payload) => {
    const slot = payload.slot;
    const seq = (memorySeqRef.current.get(slot) || 0) + 1;
    memorySeqRef.current.set(slot, seq);
    memoryInFlightRef.current.set(slot, seq);
    memoryWireRef.current.set(slot, payload.wire);
    const settle = (ok) => {
      if (memorySeqRef.current.get(slot) !== seq) return; // superseded body
      memoryInFlightRef.current.delete(slot);
      if (!ok) memoryWireRef.current.delete(slot);
      const queued = memoryQueuedRef.current.get(slot);
      if (queued) {
        memoryQueuedRef.current.delete(slot);
        issueMemory(queued);
      }
    };
    putPageState(payload.page, payload.lens, JSON.parse(payload.wire)).then(
      () => settle(true),
      () => settle(false),
    );
  }, []);

  const commitMemory = useCallback(
    (payload) => {
      if (memoryPendingRef.current?.slot === payload.slot) {
        memoryPendingRef.current = null;
      }
      if (memoryInFlightRef.current.get(payload.slot)) {
        memoryQueuedRef.current.set(payload.slot, payload);
        return;
      }
      issueMemory(payload);
    },
    [issueMemory],
  );

  const memoryWire = useMemo(
    () => (currentPage ? pageDimsWire(currentPage, pageDims[currentPage]) : null),
    [currentPage, pageDims],
  );

  useEffect(() => {
    // Writes wait for the slot's GET: a debounce that fired first would PUT the
    // scene's defaults over the memory this page entry is still fetching.
    if (!serverReady || !slotId || memoryWire == null) return undefined;
    if (memoryTimerRef.current) clearTimeout(memoryTimerRef.current);
    // An edit made just before a lens/page switch belongs to the slot it was
    // made under — commit it there rather than dropping or misfiling it. This
    // runs BEFORE the load gate: the new slot may still be fetching, and the
    // cleanup above has already cancelled the old slot's timer.
    const pending = memoryPendingRef.current;
    if (pending && pending.slot !== slotId) {
      memoryPendingRef.current = null;
      commitMemory(pending);
    }
    if (!memoryLoadedRef.current.has(slotId)) return undefined;
    memoryPendingRef.current = null;
    if (memoryWireRef.current.get(slotId) === memoryWire) {
      memoryQueuedRef.current.delete(slotId); // revert-within-window (R1)
      return undefined;
    }
    const payload = {
      slot: slotId,
      page: currentPage,
      lens: lensSlot,
      wire: memoryWire,
    };
    memoryPendingRef.current = payload;
    memoryTimerRef.current = setTimeout(
      () => commitMemory(payload),
      SAVE_DEBOUNCE_MS,
    );
    return () => clearTimeout(memoryTimerRef.current);
  }, [serverReady, slotId, currentPage, lensSlot, memoryWire, memoryLoadTick,
    commitMemory]);

  // The provider mounts above the LENS ROUTES only (§46.1 P2: map + graph, one
  // shared instance — moving between them no longer unmounts it), so leaving
  // for any other page still unmounts it mid-debounce; without this the edit
  // lives only in this device's mirror and layer (c) — whose whole point is
  // other devices
  // — silently drops it. Tab-hide is best effort: an in-flight XHR can still
  // die with the tab, but the flush is strictly better than the cancel.
  useEffect(() => {
    const flush = () => {
      const memory = memoryPendingRef.current;
      if (memory) {
        memoryPendingRef.current = null;
        if (memoryTimerRef.current) clearTimeout(memoryTimerRef.current);
        commitMemory(memory);
      }
      const pending = pendingSaveRef.current;
      if (!pending) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      commitSave(pending);
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      flush();
    };
  }, [commitSave, commitMemory]);

  const value = useMemo(
    () => ({
      eventTypes,
      setEventTypes,
      regions,
      setRegions,
      regionNames,
      setRegionNames,
      timeRange,
      setTimeRange,
      customDateRange,
      setCustomDateRange,
      activePreset,
      setActivePreset,
      presetKeywords,
      setPresetKeywords,
      search,
      setSearch,
      curation,
      setCuration,
      // §48.1/§48.2 C — THE BRUSH, written by whichever TimelineStrip is
      // mounted and read by every scene as the visible time slice.
      brush, // {from, to} | null — null = the WHOLE loaded window
      setBrush, // value | updater; clamped to the window on every path
      clearBrush, // back to whole-window
      brushWindow, // {min, max} | null — the loaded span the strip reported
      setBrushWindow, // (min, max) — the strip reports its span; re-clamps
      playbackRolling, // the roll animation is running
      setPlaybackRolling,
      playbackSpeed,
      setPlaybackSpeed,
      // DERIVED point-playhead — the pre-C reads, unchanged in meaning:
      // `playheadTs` is the brush's LEADING EDGE (null = no slice) and
      // `playbackActive` is "a slice is engaged", which is exactly B's
      // invariant (active ⟺ playheadTs !== null). Scenes migrating to the
      // range filter read `brush` instead of both.
      playheadTs: brush ? brush.to : null,
      playbackActive: brush !== null,
      pageDims,
      setPageDim,
      seedPageDim,
      registerPage,
      currentPage,
      entityTypes,
      setEntityTypes,
      presets,
      presetsReady,
      refreshPresets,
      appliedSnapshot,
      setAppliedSnapshot,
      markApplied,
      modified,
      serverReady,
      // {field: {sent, kept, cap}} | null — the last durable write that hit a
      // server cap. The lists above already hold the stored truth; this is what
      // is left to SAY about it.
      lensTruncated,
    }),
    [eventTypes, regions, regionNames, timeRange, customDateRange, activePreset,
      presetKeywords, search, curation,
      brush, setBrush, clearBrush, brushWindow, setBrushWindow,
      playbackRolling, setPlaybackRolling, playbackSpeed, setPlaybackSpeed,
      pageDims, setPageDim, seedPageDim,
      registerPage, currentPage, entityTypes,
      setEntityTypes, presets, presetsReady, refreshPresets, appliedSnapshot,
      markApplied, modified, serverReady, lensTruncated],
  );

  return <LensContext.Provider value={value}>{children}</LensContext.Provider>;
};

// Scenes outside the shell (non-lens routes) get null — callers that need the
// lens are mounted inside it by construction, so this throws loudly instead
// of silently rendering an unfiltered world.
export const useLens = () => {
  const ctx = useContext(LensContext);
  if (!ctx) throw new Error("useLens() must be used inside <LensProvider>");
  return ctx;
};

// The mounted scene declares itself so memories know which slot to load and
// save. §48.2 B: useLensPageChrome() calls this from the PAGE (the chrome
// outlives every scene now, so it cannot answer "which page is mounted"); a
// page that registers no chrome must call it directly.
export const useLensPage = (page) => {
  const { registerPage } = useLens();
  useEffect(() => registerPage(page), [registerPage, page]);
};

// [dims, setDim] for one page's whitelist — the page-section accessor. setDim
// takes a value or an updater, exactly like useState, so a migrated control's
// call sites do not change.
export const usePageDims = (page) => {
  const { pageDims, setPageDim } = useLens();
  const setDim = useCallback(
    (key, value) => setPageDim(page, key, value),
    [page, setPageDim],
  );
  return [pageDims[page], setDim];
};

export default LensProvider;
